import { useQueries } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  type ListApprovalsResponse,
  type SwiftTransfer,
  type UnifiedTransfer,
  approvalsListApprovals,
  bankAccountGetBankAccountOptions,
  counterpartyGetCounterpartyOptions,
  transferAchGetTransferOptions,
  transferCheckGetCheckTransferOptions,
  transferRealtimeGetTransferOptions,
  transferSwiftGetTransferOptions,
  transferWireGetWireTransferOptions,
} from '@column/api';
import { Button, Fade, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { TransferPageHeader } from '../../_components/Layout';
import { CopyRow, CustomFieldWrapper } from '~/app/pages/Transfers/_components/CopyRow';
import {
  type ReviewQuoteTransfer,
  useInternationalWireReviewQuote,
} from '~/app/pages/Transfers/_hooks/useInternationalWireReviewQuote';
import { getInternationalWireReviewFXQuoteDetails } from '~/app/pages/Transfers/_utils/internationalWire';
import {
  type ApprovalHoldRequest,
  isApprovalHold,
  isInternationalWireApprovalBlocked,
} from '~/app/pages/Transfers/_utils/transferReview';
import { RenderFields } from '~/components';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { Card, Divider, Flex, Inner, Text } from '~/styles';
import { TransferReviewDecision } from '~/typings/enum';

import { PageCounter } from './PageCounter';

export type ReviewTransferFormValues = ApprovalHoldRequest & { fx_quote_id?: string };

const ReviewQuoteFieldWrapper = styled(CustomFieldWrapper)`
  display: flex;
  align-self: stretch;
  align-items: center;
  padding: 0 4px 0 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 0 8px 0 36px;
  }
`;

interface TransferReviewModalProps {
  transfers: UnifiedTransfer[];
  transferStartIndex: number;
  currentIndex: number;
  totalTransfers: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    transferId: string,
    transferType: TransferEntryType,
    transferStatus: string,
    request: ReviewTransferFormValues
  ) => Promise<void>;
}

const MODAL_PAGE_SIZE = 10;
const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => entry.transfer) ?? [];

const getCreatedByName = (createdBy: unknown): string | undefined => {
  if (!createdBy || typeof createdBy !== 'object') return undefined;
  const { name } = createdBy as { name?: unknown };
  return typeof name === 'string' ? name : undefined;
};

const getCheckPayeeName = (checkTransfer: unknown): string | undefined => {
  if (!checkTransfer || typeof checkTransfer !== 'object') return undefined;
  const { payee_name: payeeName } = checkTransfer as { payee_name?: unknown };
  return typeof payeeName === 'string' ? payeeName : undefined;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;
  gap: 4px;
  padding: 12px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px 0;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: auto min-content;
    align-items: center;
    gap: 0;
  }
`;

const ViewTransferButton = styled(Button)`
  padding: 0;
  margin-bottom: 3px;
`;

const EmptyStateWrapper = styled(Fade)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 24px);

  div {
    height: 100%;
  }
`;

const KeyboardArrow = styled.div`
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(300)};
  padding: 3px;
  border-radius: 4px;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    width: 12px;
    height: 12px;
  }
`;

const LoadingContainer = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const ModalBody = styled.div`
  position: relative;
  min-height: 440px;
`;

const AnimatedButtonContainer = styled.div<{ animationState: 'in' | 'out' | undefined }>`
  position: relative;
  display: inline-block;

  transition: all 0.3s ease;
  transform: translate(${({ animationState }) => (!!animationState ? '0, -4px' : '0, 0')});

  svg {
    --icon-color: ${({ theme }) => theme.white.background};
  }

  .button-content {
    opacity: ${({ animationState }) => (animationState === 'in' ? 0 : 1)};
    transition: opacity 0.3s ease;
    transition-delay: ${({ animationState }) => (!!animationState ? '0.3s' : '0')};
    visibility: ${({ animationState }) => (animationState === 'in' ? 'hidden' : 'visible')};
  }

  .icon {
    position: absolute;
    pointer-events: none;
    top: 50%;
    left: 50%;
    transform: translate(${({ animationState }) => (!!animationState ? '-50%, -50% ' : '-50%, -25%')})
      scale(${({ animationState }) => (animationState === 'in' ? 1.2 : animationState === 'out' ? 1.8 : 0.6)});
    opacity: ${({ animationState }) => (animationState === 'in' ? 1 : 0)};
    transition: all 0.3s ease;
  }
`;

const StyledCard = styled(Card)`
  .transfer-page-header {
    padding: 16px;
  }
`;

interface AnimatedButtonProps {
  onClick: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  animationState: 'in' | 'out' | undefined;
  children: React.ReactNode;
  icon: React.ReactNode;
  variant?: 'primary' | 'danger';
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  onClick,
  isLoading,
  isDisabled,
  animationState,
  children,
  icon,
  variant,
}) => (
  <AnimatedButtonContainer animationState={animationState}>
    <Button variant={variant} size="small" isLoading={isLoading} isDisabled={isDisabled} onClick={onClick}>
      <span className="button-content">{children}</span>
    </Button>
    <div className="icon">{icon}</div>
  </AnimatedButtonContainer>
);

const getOptionKey = () => {
  const isMac = navigator.userAgent.includes('Mac');
  return isMac ? '⌥' : 'Alt+';
};

export const TransferReviewModal: React.FC<TransferReviewModalProps> = ({
  transfers: transfersProp,
  transferStartIndex,
  currentIndex: currentIndexProp,
  totalTransfers: totalTransfersProp,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [isSubmitting, setIsSubmitting] = useState<TransferReviewDecision>();
  const [showContent, setShowContent] = useState(true);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('up');
  const formMethods = useForm<ReviewTransferFormValues>();
  const comment = formMethods.watch('comment');
  const commentFocused = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(currentIndexProp);
  const [transfers, setTransfers] = useState<(UnifiedTransfer | undefined)[]>([]);
  const [isFetchingMoreTransfers, setIsFetchingMoreTransfers] = useState(false);
  const adjacentTransfers = useMemo(() => {
    const seen = new Set<string>();
    return [transfers[currentIndex], transfers[currentIndex - 1], transfers[currentIndex + 1]].filter(
      (transfer): transfer is UnifiedTransfer => {
        if (!transfer || seen.has(transfer.id)) return false;
        seen.add(transfer.id);
        return true;
      }
    );
  }, [currentIndex, transfers]);
  const transferDetailQueries = useMemo(
    () =>
      adjacentTransfers
        .map((transfer) => {
          if (transfer.type === 'ach_credit' || transfer.type === 'ach_debit') {
            return {
              id: transfer.id,
              options: transferAchGetTransferOptions({ path: { ach_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'wire') {
            return {
              id: transfer.id,
              options: transferWireGetWireTransferOptions({ path: { wire_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'check_debit') {
            return {
              id: transfer.id,
              options: transferCheckGetCheckTransferOptions({ path: { check_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'realtime') {
            return {
              id: transfer.id,
              options: transferRealtimeGetTransferOptions({ path: { realtime_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'swift') {
            return {
              id: transfer.id,
              options: transferSwiftGetTransferOptions({ path: { swift_transfer_id: transfer.id } }),
            };
          }
          return undefined;
        })
        .filter((query): query is NonNullable<typeof query> => Boolean(query)),
    [adjacentTransfers]
  );
  const transferDetailResults = useQueries({
    queries: transferDetailQueries.map(({ options }) => ({
      ...options,
      enabled: isOpen,
    })),
  });
  const bankAccountIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const bankAccountId = transfer.sender_internal_account?.bank_account_id;
      if (bankAccountId) seen.add(bankAccountId);
    });
    return Array.from(seen);
  }, [adjacentTransfers]);
  const bankAccountResults = useQueries({
    queries: bankAccountIds.map((bankAccountId) => ({
      ...bankAccountGetBankAccountOptions({ path: { bank_account_id: bankAccountId } }),
      enabled: isOpen,
    })),
  });
  const counterpartyIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const counterpartyId = transfer.external_destination?.counterparty_id;
      if (counterpartyId) seen.add(counterpartyId);
    });
    return Array.from(seen);
  }, [adjacentTransfers]);
  const counterpartyResults = useQueries({
    queries: counterpartyIds.map((counterpartyId) => ({
      ...counterpartyGetCounterpartyOptions({ path: { counterparty_id: counterpartyId } }),
      enabled: isOpen,
    })),
  });
  const creators = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    // Seed from the approvals row so there's a name before the detail query resolves.
    adjacentTransfers.forEach((transfer) => {
      next[transfer.id] = getCreatedByName(transfer.created_by);
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const detail = transferDetailResults[index]?.data;
      const detailCreator = detail && getCreatedByName((detail as { created_by?: unknown }).created_by);
      if (detailCreator) next[id] = detailCreator;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const checkPayees = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    adjacentTransfers.forEach((transfer) => {
      if (transfer.type === 'check_debit') {
        next[transfer.id] = transfer.external_destination?.receiver_name;
      }
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const payeeName = getCheckPayeeName(transferDetailResults[index]?.data);
      if (payeeName) next[id] = payeeName;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const bankAccounts = useMemo(() => {
    const next: Record<string, (typeof bankAccountResults)[number]['data']> = {};
    bankAccountIds.forEach((id, index) => {
      const bankAccount = bankAccountResults[index]?.data;
      if (bankAccount) next[id] = bankAccount;
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const bankAccountsLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    bankAccountIds.forEach((id, index) => {
      const result = bankAccountResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const counterparties = useMemo(() => {
    const next: Record<string, (typeof counterpartyResults)[number]['data']> = {};
    counterpartyIds.forEach((id, index) => {
      const counterparty = counterpartyResults[index]?.data;
      if (counterparty) next[id] = counterparty;
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const counterpartiesLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    counterpartyIds.forEach((id, index) => {
      const result = counterpartyResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const currentData = useMemo(() => {
    const transfer = transfers[currentIndex];
    const bankAccountId = transfer?.sender_internal_account?.bank_account_id;
    const counterpartyId = transfer?.external_destination?.counterparty_id;

    return {
      bankAccount: bankAccountId ? bankAccounts[bankAccountId] : undefined,
      bankAccountId,
      counterparty: counterpartyId ? counterparties[counterpartyId] : undefined,
      counterpartyId,
      checkPayee: transfer?.id ? checkPayees[transfer.id] : undefined,
      creator: transfer?.id ? creators[transfer.id] : undefined,
      transfer,
    };
  }, [bankAccounts, checkPayees, counterparties, creators, currentIndex, transfers]);
  const currentTransferDetailResult = useMemo(() => {
    if (currentData.transfer?.type !== 'swift') return;
    const detailIndex = transferDetailQueries.findIndex(({ id }) => id === currentData.transfer?.id);
    return detailIndex >= 0 ? transferDetailResults[detailIndex] : undefined;
  }, [currentData.transfer, transferDetailQueries, transferDetailResults]);
  const currentSwiftTransferDetail = currentTransferDetailResult?.data as SwiftTransfer | undefined;
  const isCurrentInternationalWireApprovalBlocked = isInternationalWireApprovalBlocked(
    currentData.transfer?.type,
    currentTransferDetailResult
      ? {
          hasData: Boolean(currentSwiftTransferDetail),
          isError: currentTransferDetailResult.isError,
          isFetching: currentTransferDetailResult.isFetching,
          isLoading: currentTransferDetailResult.isLoading,
        }
      : undefined
  );
  const reviewQuoteTransfer = useMemo<ReviewQuoteTransfer | undefined>(() => {
    const transfer = currentData.transfer;
    if (transfer?.type !== 'swift') return;
    if (isApprovalHold(transfer.status)) return;
    if (isCurrentInternationalWireApprovalBlocked || !currentSwiftTransferDetail) return;
    return {
      amount: currentSwiftTransferDetail.amount,
      currencyCode: currentSwiftTransferDetail.currency_code,
      fxQuoteId: currentSwiftTransferDetail.fx_quote_id,
      id: transfer.id,
      instructedAmount: currentSwiftTransferDetail.instructed_amount,
      instructedCurrencyCode: currentSwiftTransferDetail.instructed_currency_code,
    };
  }, [currentData.transfer, currentSwiftTransferDetail, isCurrentInternationalWireApprovalBlocked]);
  const {
    isFXTransfer: isCurrentFXTransfer,
    isLoading: isFXQuoteLoading,
    quote: currentFXQuote,
    refreshQuote: requestFXQuote,
  } = useInternationalWireReviewQuote(reviewQuoteTransfer, isOpen);
  const emptyState = transfers.length === 0;
  const isLoadingAdditionalData =
    isOpen &&
    !emptyState &&
    !!currentData.transfer &&
    ((!!currentData.bankAccountId && !currentData.bankAccount && bankAccountsLoading[currentData.bankAccountId]) ||
      (!!currentData.counterpartyId && !currentData.counterparty && counterpartiesLoading[currentData.counterpartyId]));
  useEffect(() => {
    if (!isLoadingAdditionalData) {
      setShowContent(true);
    }
  }, [isLoadingAdditionalData]);
  useEffect(() => {
    if (!isOpen) return;
    commentFocused.current = false;
    const arr: (UnifiedTransfer | undefined)[] = Array(totalTransfersProp).fill(undefined);
    transfersProp.forEach((transfer, i) => {
      const slot = transferStartIndex + i;
      if (slot < arr.length) arr[slot] = transfer;
    });
    setTransfers(arr);
    setCurrentIndex(currentIndexProp);
  }, [isOpen]);

  const fetchSlot = useCallback(
    async (idx: number) => {
      setIsFetchingMoreTransfers(true);
      try {
        let cursorIdx = idx - 1;
        const snapshot = transfers;
        while (cursorIdx >= 0 && !snapshot[cursorIdx]) cursorIdx--;
        const cursor = cursorIdx >= 0 ? snapshot[cursorIdx]!.id : undefined;

        const response = await approvalsListApprovals({
          query: { status: 'pending', starting_after: cursor, limit: MODAL_PAGE_SIZE },
          throwOnError: true,
        });
        const fetched = extractTransfers(response.data);
        const startIdx = cursorIdx + 1;
        setTransfers((previous) => {
          const arr = previous.slice();
          fetched.forEach((transfer, i) => {
            if (startIdx + i < arr.length) arr[startIdx + i] = transfer;
          });
          return arr;
        });
      } finally {
        setIsFetchingMoreTransfers(false);
      }
    },
    [transfers]
  );

  const handlePrevious = useCallback(() => {
    const prevIndex = currentIndex - 1;
    if (prevIndex < 0 || !transfers[prevIndex]) return;

    commentFocused.current = false;
    setSlideDirection('down');
    setShowContent(false);

    setTimeout(() => {
      formMethods.reset();
      setCurrentIndex(prevIndex);
      setTimeout(() => setShowContent(true), 100);
    }, 100);
  }, [currentIndex, formMethods, transfers]);

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= transfers.length) return;

    commentFocused.current = false;
    setSlideDirection('up');
    setShowContent(false);

    setTimeout(async () => {
      if (!transfers[nextIndex]) await fetchSlot(nextIndex);
      formMethods.reset();
      setCurrentIndex(nextIndex);
      setTimeout(() => setShowContent(true), 100);
    }, 100);
  }, [currentIndex, fetchSlot, formMethods, transfers]);

  const [successAnimation, setSuccessAnimation] = useState<'in' | 'out' | undefined>(undefined);
  const [rejectAnimation, setRejectAnimation] = useState<'in' | 'out' | undefined>(undefined);

  const handleSubmit = useCallback(
    async (decision: TransferReviewDecision) => {
      if (!currentData.transfer) return;
      if (decision === TransferReviewDecision.Approved && isCurrentInternationalWireApprovalBlocked) return;
      if (decision === TransferReviewDecision.Approved && isCurrentFXTransfer && !currentFXQuote) return;

      setIsSubmitting(decision);

      try {
        await onSubmit(
          currentData.transfer.id,
          currentData.transfer.type as TransferEntryType,
          currentData.transfer.status,
          {
            comment,
            decision,
            fx_quote_id:
              decision === TransferReviewDecision.Approved && isCurrentFXTransfer ? currentFXQuote?.id : undefined,
          }
        );

        if (decision === TransferReviewDecision.Approved) {
          setSuccessAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setSuccessAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        } else if (decision === TransferReviewDecision.Rejected) {
          setRejectAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setRejectAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        if (
          decision === TransferReviewDecision.Approved &&
          isCurrentFXTransfer &&
          (error as { code?: string })?.code === 'fx_quote_expired'
        ) {
          await requestFXQuote();
          return;
        }
        throw error;
      } finally {
        setIsSubmitting(undefined);
        setSuccessAnimation(undefined);
        setRejectAnimation(undefined);
      }

      setTransfers((previous) => {
        const updated = previous.slice();
        updated.splice(currentIndex, 1);
        setCurrentIndex((existingIndex) => Math.min(existingIndex, Math.max(updated.length - 1, 0)));
        return updated;
      });
      formMethods.reset();
    },
    [
      comment,
      currentData,
      currentFXQuote,
      currentIndex,
      formMethods,
      isCurrentFXTransfer,
      isCurrentInternationalWireApprovalBlocked,
      onSubmit,
      requestFXQuote,
    ]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isAltOption = e.altKey;

      if (isLoadingAdditionalData || isSubmitting || isFetchingMoreTransfers) return;

      if (isAltOption) {
        if (e.code === 'KeyA') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Approved);
        }
        if (e.code === 'KeyR') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Rejected);
        }
      } else if (!commentFocused.current) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleNext();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handlePrevious();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    isLoadingAdditionalData,
    isSubmitting,
    isFetchingMoreTransfers,
    handleSubmit,
    handleNext,
    handlePrevious,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Large} withClose={false}>
      <Modal.Content align="left">
        <Grid>
          <Modal.Headline>Review Transfer</Modal.Headline>
          <Fade show={!emptyState} direction={slideDirection === 'up' ? 'right' : 'left'}>
            <Flex alignItems="center" flexDirection="row" gap={6} justifyContent="space-between">
              <Flex alignItems="center" flexDirection="row" gap={12}>
                <Flex alignItems="center" flexDirection="row" gap={6}>
                  <Text size={12} color="lighter">
                    Previous
                  </Text>
                  <KeyboardArrow>
                    <Icon.ArrowLeft />
                  </KeyboardArrow>
                </Flex>
                <Flex alignItems="center" flexDirection="row" gap={6}>
                  <KeyboardArrow>
                    <Icon.ArrowRight />
                  </KeyboardArrow>
                  <Text size={12} color="lighter">
                    Next
                  </Text>
                </Flex>
              </Flex>
              <PageCounter
                current={currentIndex + 1}
                total={transfers.length}
                direction={slideDirection}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </Flex>
          </Fade>
        </Grid>
      </Modal.Content>

      <Divider />

      <Modal.Content align="left">
        <ModalBody>
          <LoadingContainer
            show={(transfers.length > 0 && !currentData.transfer) || isLoadingAdditionalData || isFetchingMoreTransfers}
          >
            <LogoLoading />
          </LoadingContainer>

          <EmptyStateWrapper show={emptyState} distance={24} direction="right">
            <Flex alignItems="center" flexDirection="column" gap={8} justifyContent="center">
              <Text size={18} weight="medium" color="foreground">
                All caught up
              </Text>
              <Text size={14} color="light">
                There are no more transfers to review
              </Text>
            </Flex>
          </EmptyStateWrapper>

          <Fade
            key={currentIndex}
            show={!emptyState && !isLoadingAdditionalData && showContent}
            distance={16}
            direction={slideDirection === 'up' ? 'right' : 'left'}
          >
            <Inner px={0} pt="var(--page-margin)" pb={0}>
              <Flex flexDirection="column" gap={24}>
                <StyledCard>
                  <TransferPageHeader
                    className="transfer-page-header"
                    action={
                      <ViewTransferButton
                        icon={<Icon.ChevronRightLarge />}
                        iconRight
                        variant="subtle"
                        onClick={() =>
                          currentData.transfer &&
                          navigate(
                            getTransferURL(currentData.transfer.id, currentData.transfer.type as TransferEntryType)
                          )
                        }
                      >
                        View Transfer
                      </ViewTransferButton>
                    }
                    amount={currentFXQuote?.sellAmount ?? currentData.transfer?.amount ?? 0}
                    createdAt={currentData.transfer?.created_at}
                    currencyCode={currentFXQuote?.sellCurrencyCode ?? currentData.transfer?.currency_code}
                    direction={{ type: 'outgoing' }}
                    isAdding={
                      currentData.transfer?.type
                        ? convertTransferAmount(
                            currentData.transfer.type as TransferEntryType,
                            currentData.transfer.is_incoming
                          ) > 0
                        : undefined
                    }
                    settledCurrencyAmount={currentSwiftTransferDetail?.settled_amount}
                    settledCurrencyCode={currentSwiftTransferDetail?.settled_currency_code}
                    transferType={currentData.transfer ? (currentData.transfer.type as TransferEntryType) : 'wire'}
                  />
                  <CopyRow label="Account" value={currentData.bankAccount?.description || '-'} />
                  <CopyRow
                    label={currentData.transfer?.type === 'check_debit' ? 'Payee' : 'Counterparty'}
                    value={currentData.checkPayee || currentData.counterparty?.name || '-'}
                  />
                  <CopyRow label="Created By" value={currentData.creator || '-'} />
                  <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
                  {isCurrentFXTransfer &&
                    getInternationalWireReviewFXQuoteDetails({
                      context: 'copy-row',
                      isLoading: isFXQuoteLoading,
                      isSandbox: !!isSandbox,
                      onRetry: requestFXQuote,
                      quote: currentFXQuote,
                    }).map(({ label, value }) => (
                      <CopyRow
                        key={label}
                        label={label}
                        value={
                          typeof value === 'string' ? value : <ReviewQuoteFieldWrapper>{value}</ReviewQuoteFieldWrapper>
                        }
                      />
                    ))}
                </StyledCard>

                <FormProvider {...formMethods}>
                  {RenderFields<ReviewTransferFormValues>({
                    sections: [
                      {
                        variant: 'isCompact',
                        fields: [
                          {
                            id: 'comment',
                            label: 'Comment',
                            oneColumn: true,
                            children: ({ onChange, value }, { error }) => (
                              <Input
                                isDisabled={!!isSubmitting}
                                onChange={onChange}
                                onFocus={() => (commentFocused.current = true)}
                                onBlur={() => (commentFocused.current = false)}
                                placeholder="Add a comment..."
                                value={value as string}
                                hasError={!!error}
                              />
                            ),
                          },
                        ],
                      },
                    ],
                  })}
                </FormProvider>
              </Flex>
            </Inner>
          </Fade>
        </ModalBody>
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Flex alignItems="center" flexDirection="row" gap={12} justifyContent="flex-end" auto>
          <AnimatedButton
            onClick={() => handleSubmit(TransferReviewDecision.Rejected)}
            isLoading={isSubmitting === TransferReviewDecision.Rejected && !rejectAnimation}
            isDisabled={isSubmitting === TransferReviewDecision.Approved || emptyState}
            animationState={rejectAnimation}
            icon={<Icon.Cross />}
            variant="danger"
          >
            Reject <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}R</span>
          </AnimatedButton>
          <AnimatedButton
            onClick={() => handleSubmit(TransferReviewDecision.Approved)}
            isLoading={isSubmitting === TransferReviewDecision.Approved && !successAnimation}
            isDisabled={
              isSubmitting === TransferReviewDecision.Rejected ||
              emptyState ||
              isCurrentInternationalWireApprovalBlocked ||
              (isCurrentFXTransfer && (isFXQuoteLoading || !currentFXQuote))
            }
            animationState={successAnimation}
            icon={<Icon.Checkmark />}
          >
            Approve <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}A</span>
          </AnimatedButton>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
